/**
 * LegsPanel — the workspace's middle column: the basket being built, and the open
 * baskets being managed.
 *
 * NEW legs: include ✓, BUY/SELL, strike stepper, CE/PE, lots, price (MKT follows the live
 * mark; LMT is a price you type), expiry. Strike controls move the whole new structure
 * (Shift / Width / Hedge, + Add wings), Match premiums balances a short strangle.
 *
 * HELD legs (loaded from an OPEN basket) are priced at their fill and analysed with
 * everything else. Each can be ROLLED (PAPER or LIVE): the held leg is marked CLOSE
 * (booked at its live mark in the payoff) and a replacement leg appears to step to a new
 * strike; "Execute adjustment" sends close + open to the basket's adjustment endpoint.
 * "Suggest roll" pre-builds the playbook roll (close the cheaper short, re-sell the strike
 * whose premium matches the dearer one, never past the straddle).
 *
 * Orders: LIVE (real Delta orders) is the default for a new basket; PAPER is one click away,
 * and an account without an active Delta key starts on PAPER with LIVE disabled. New baskets →
 * /baskets/from-legs (mode) → approve. Adjustments and closes follow the basket's own mode
 * (LIVE ones ask for a confirmation first).
 *
 * Open positions: choosing one SHOWS THAT POSITION ONLY (other loaded positions are dropped;
 * "+" beside a row adds it alongside for a combined view). With exactly one position loaded,
 * new legs (e.g. "+ Add wings" on its shorts) can be ADDED TO IT — they go through the basket's
 * adjustment endpoint (open-only), so it stays one basket with one P&L and exit plan.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, IconButton, Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import toast from 'react-hot-toast';
import { optionsService } from '../../../services/options.service';
import {
  WsLeg, WsStats, OptType, Side, classifyLegs, lotsGcd, shiftLegs, widenLegs, hedgeLegs, structureReadout,
  isNewLeg, shortPair, matchStrike, stepStrike, feeUsd, netPremium, computeStats,
} from './wsMath';
import { WsChain, READY_MADE, Outlook, buildReadyMade, deltaSymbol, parseDeltaSymbol, newLegId, markOf, execPrice, sideQuotes, fetchBookFills, BookFillRow } from './wsData';
import { WsColors, Ccy, fmtMoney, fmtPx, fmtStrike, expLabel, WsPrefs } from './wsUi';
import { USD_TO_INR } from '../../../utils/constants';
import PlaybookCard from './PlaybookCard';
import { brokerFundsService } from '../../../services/brokerFunds.service';
import { builtNetPremium, TplBuiltLeg } from '../../../utils/optionTemplates';

interface Props {
  base: string;
  chain: WsChain | null;
  legs: WsLeg[];
  setLegs: (fn: (prev: WsLeg[]) => WsLeg[]) => void;
  stats: WsStats;
  spot: number;
  atmIv: number;
  ccy: Ccy;
  c: WsColors;
  prefs: WsPrefs;
  setPrefs: (p: Partial<WsPrefs>) => void;
  /** Show this contract in the chart strip. */
  onChartContract: (symbol: string) => void;
  /** Switch the chain to an expiry (rolls need that expiry's strikes). */
  viewExpiry: (iso: string) => void;
  compact?: boolean;          // Classic view: no ready-made cards (shown as chips instead)
  /** Show the combined premium of these legs as candles in the chart strip. */
  onChartBasket?: () => void;
}

type Cost = 'Credit' | 'Debit';
/** Fallback when the chain can't build a card right now (ratios depend on live prices). */
const STATIC_COST: Record<string, Cost> = {
  long_call: 'Debit', bull_call_spread: 'Debit', long_put: 'Debit', bear_put_spread: 'Debit', long_straddle: 'Debit', long_strangle: 'Debit',
  bull_put_spread: 'Credit', bear_call_spread: 'Credit', monthly_strangle: 'Credit', short_strangle: 'Credit', iron_condor: 'Credit',
  iron_butterfly: 'Credit', short_straddle: 'Credit', call_ratio: 'Credit', put_ratio: 'Credit',
};

const MAX_LOTS = 10000;
const GRID = '18px 24px minmax(84px,1.2fr) 26px 52px minmax(66px,1fr) 44px 68px';
/** With held legs on screen: a P&L column after the price (what each leg makes now). */
const GRID_PNL = '16px 22px minmax(66px,1.1fr) 24px 52px minmax(50px,1fr) minmax(54px,0.9fr) 40px 58px';
/** A narrow column (e.g. 1280 px screens, ~310 px): tighter cells, and a held leg's P&L moves to a
 *  line of its own under the row so the expiry and the roll / close buttons stay reachable. */
const GRID_NARROW = '14px 22px minmax(56px,1fr) 22px 46px minmax(46px,0.9fr) 36px 40px';
const NARROW_PX = 430;
/** Number inputs keep Chrome's spinner box even under `all: unset`, which clipped "1000" to "100(". */
const NO_SPIN = { MozAppearance: 'textfield', '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 } } as const;

/** Margin an open basket holds (GET /options/baskets/margin). */
export interface HeldMargin { source: 'BROKER' | 'ESTIMATE'; marginInr: number | null; estimateInr: number | null; asOf: number; reason?: string | null }

/** Repeat each NEW leg lots/g times and send g lots — how the backend expresses ratios. */
function expandForOrder(legs: WsLeg[]) {
  const live = legs.filter(l => l.enabled && isNewLeg(l));
  const g = lotsGcd(live.map(l => l.lots));
  const out: Array<{ action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number; expiry: string }> = [];
  for (const l of live) for (let i = 0; i < l.lots / g; i++) out.push({ action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry });
  return { legs: out, lots: g };
}

/** Exit rules for an OPEN basket: fixed ₹ target/stop on total P&L, and the ratio alert.
 *  A top-level component so its form state survives the parent's 3-second re-renders. */
const ExitRulesDialog: React.FC<{ id: number; b: any; cv: number; prefs: WsPrefs; c: WsColors; onClose: () => void; onSaved: () => void }> = ({ id, b, cv, prefs, c, onClose, onSaved }) => {
  const cvB = Number(b?.legFills?.[0]?.contractValue) || cv;
  const credit = b?.entryCredit != null ? Number(b.entryCredit) * cvB * USD_TO_INR : 0;
  const [mode, setMode] = useState<'FIXED' | 'PREMIUM'>('FIXED');
  const [tp, setTp] = useState<number>(b?.tpAmountInr > 0 ? Number(b.tpAmountInr) : Math.max(0, Math.round(credit * prefs.tpPctOfCredit / 100)));
  // Stop as a SIGNED total-P&L level (−47,355 = a loss stop; +12,000 = a profit floor). Stored as slAmountInr = −stopAt.
  const [stopAt, setStopAt] = useState<number>(Number(b?.slAmountInr) ? -Number(b.slAmountInr) : -Math.max(0, Math.round(credit * prefs.slPctOfCredit / 100)));
  const [ra, setRa] = useState<boolean>(true);
  const [rx, setRx] = useState<number>(b?.ratioAlertX > 0 ? Number(b.ratioAlertX) : prefs.ratioX);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await optionsService.updateBasketLevels(id, {
        exitMode: mode,
        ...(mode === 'FIXED' ? { tpAmountInr: tp, slAmountInr: -stopAt } : {}),
        ratioAlertX: ra ? rx : null,
      });
      toast.success(`Exit rules saved for #${id}`);
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not save');
    } finally { setSaving(false); }
  };
  const num = (v: number, set: (n: number) => void) => (
    <Box component="input" type="number" value={v} min={0} onChange={(e: any) => set(Math.max(0, Number(e.target.value) || 0))}
      sx={{ width: 110, p: 0.5, borderRadius: 0.8, border: `1px solid ${c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />
  );
  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>Exit rules · #{id} {b?.strategy}</DialogTitle>
      <DialogContent dividers sx={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['FIXED', 'PREMIUM'] as const).map(m => (
            <Box key={m} component="button" type="button" onClick={() => setMode(m)}
              sx={{ all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center', py: 0.6, borderRadius: 1, fontWeight: 700, fontSize: 12,
                border: `1px solid ${mode === m ? c.amber : c.border}`, color: mode === m ? c.amber : c.dim }}>
              {m === 'FIXED' ? 'Fixed ₹ on net total P&L' : 'Premium levels (net)'}
            </Box>
          ))}
        </Box>
        {mode === 'FIXED' ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ flex: 1 }}>Take profit at net total P&L of +₹</Box>{num(tp, setTp)}</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1 }}>Stop at net total P&L of ₹ <Box component="span" sx={{ color: c.faint, fontSize: 11 }}>(negative = loss, positive = lock a profit)</Box></Box>
              <Box component="input" type="number" data-testid="ws-exit-stop" value={stopAt} onChange={(e: any) => setStopAt(Math.round(Number(e.target.value) || 0))}
                sx={{ width: 110, p: 0.5, borderRadius: 0.8, border: `1px solid ${stopAt >= tp || stopAt === 0 ? c.red : c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />
            </Box>
            {mode === 'FIXED' && (stopAt >= tp || stopAt === 0) && <Box sx={{ fontSize: 11.5, color: c.red }}>{stopAt === 0 ? 'Move the stop off exactly ₹0.' : 'The stop must be below the target.'}</Box>}
            <Box sx={{ fontSize: 11.5, color: c.dim }}>
              Both are NET — what you receive after closing at the bid / ask and the entry and exit fees. Total P&L = what adjustments already booked + the open legs. These numbers never move on their own — not on a roll, not by a trail; drag the lines on the basket chart to move them.
              {credit > 0 && <> Entry credit was ≈ ₹{Math.round(credit).toLocaleString('en-IN')} (playbook: stop = full credit, target = 50–70% of it).</>}
            </Box>
          </>
        ) : (
          <Box sx={{ fontSize: 11.5, color: c.dim }}>Premium levels: the basket's existing % target / stop on the net premium, re-based after each adjustment (the app default).</Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input type="checkbox" checked={ra} onChange={e => setRa(e.target.checked)} style={{ accentColor: c.amber }} />
          <Box sx={{ flex: 1 }}>Telegram alert when one short's premium is</Box>
          <Box component="input" type="number" step={0.5} min={1.5} max={10} value={rx} disabled={!ra} onChange={(e: any) => setRx(Math.max(1.5, Math.min(10, Number(e.target.value) || 3)))}
            sx={{ width: 56, p: 0.5, borderRadius: 0.8, border: `1px solid ${c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />
          <span>× the other</span>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={save} disabled={saving || (mode === 'FIXED' && (!(tp > 0) || stopAt === 0 || stopAt >= tp))}>{saving ? <CircularProgress size={16} /> : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const LegsPanel: React.FC<Props> = ({ base, chain, legs, setLegs, spot, atmIv, ccy, c, prefs, setPrefs, onChartContract, viewExpiry, compact, onChartBasket }) => {
  const [mult, setMult] = useState(1);
  const [ctlOpen, setCtlOpen] = useState(false);
  // Ready-made filters: within a group any selected value matches; across groups all must.
  const [outlooks, setOutlooks] = useState<Outlook[]>([]);
  const [costs, setCosts] = useState<Cost[]>([]);
  // Order mode for NEW baskets: LIVE by default (owner, 2026-11-02); PAPER without a Delta key.
  const [orderMode, setOrderMode] = useState<'LIVE' | 'PAPER'>('LIVE');
  const [noDeltaKey, setNoDeltaKey] = useState(false);
  const [deltaAvail, setDeltaAvail] = useState<number | null>(null);
  useEffect(() => {
    let dead = false;
    brokerFundsService.getFunds().then(f => {
      if (dead || !f) return;
      if (f.status?.delta === 'no_credentials') { setNoDeltaKey(true); setOrderMode('PAPER'); }
      else if (f.status?.delta === 'ok') setDeltaAvail(Number(f.byBroker?.delta) || 0);
    }).catch(() => {});
    return () => { dead = true; };
  }, []);
  // Where new legs go: a new basket, or into the one open position on screen.
  const [target, setTarget] = useState<'new' | 'add'>('add');
  // A LIVE adjustment / close waits here for its confirmation.
  const [liveAsk, setLiveAsk] = useState<{ id: number; kind: 'adjust' | 'close' } | null>(null);
  const [openMenu, setOpenMenu] = useState<HTMLElement | null>(null);
  const [openBaskets, setOpenBaskets] = useState<any[]>([]);
  const [confirm, setConfirm] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [margin, setMargin] = useState<{ inr: number | null; err: string | null; loading: boolean }>({ inr: null, err: null, loading: false });
  const [held, setHeld] = useState<Record<number, any>>({});
  const [heldMargin, setHeldMargin] = useState<Record<number, HeldMargin>>({});
  const [menuMargin, setMenuMargin] = useState<Record<number, HeldMargin>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [exitDlg, setExitDlg] = useState<number | null>(null);
  const [closeDlg, setCloseDlg] = useState<number | null>(null);
  // new-basket exit plan (confirm dialog)
  const [exitMode, setExitMode] = useState<'FIXED' | 'PREMIUM'>('FIXED');
  const [ratioAlert, setRatioAlert] = useState(true);
  // Order-book fill preview for the confirm dialog (entry side + exit side per leg).
  const [book, setBook] = useState<{ loading: boolean; rows: Record<string, { entry: BookFillRow['fill']; exit: BookFillRow['fill']; withinCap?: number | null; capPct?: number | null }> }>({ loading: false, rows: {} });
  const scrollRef = useRef<HTMLDivElement>(null);

  const cv = chain?.contractValue || (base === 'BTC' ? 0.001 : 0.01);
  const strikesFor = useCallback((iso: string) => chain?.expiries.find(e => e.iso === iso)?.strikes || [], [chain]);
  const newLegs = legs.filter(isNewLeg);
  const enabledNew = newLegs.filter(l => l.enabled);
  const cls = classifyLegs(legs);
  const readout = structureReadout(legs);
  const heldIds = useMemo(() => Array.from(new Set(legs.filter(l => l.held && l.heldBasketId != null).map(l => l.heldBasketId as number))).sort(), [legs]);
  const heldKey = heldIds.join(',');

  // ── held baskets: live P&L, exit plan, adjustment history (one list call every 15s) ──
  const refreshHeld = useCallback(async () => {
    if (!heldIds.length) { setHeld({}); return; }
    try {
      const r: any = await optionsService.getBaskets('EXECUTED');
      const m: Record<number, any> = {};
      for (const b of r.data?.data || []) if (heldIds.includes(b.id)) m[b.id] = b;
      setHeld(m);
    } catch { /* keep the last snapshot */ }
    try {
      const mr: any = await optionsService.getBasketsMargin(heldIds);
      setHeldMargin(mr.data?.data || {});
    } catch { /* keep the last figure */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKey]);
  useEffect(() => {
    refreshHeld();
    if (!heldIds.length) return;
    const t = setInterval(() => { if (!document.hidden) refreshHeld(); }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKey, refreshHeld]);

  // ── margin preview (debounced on STRUCTURE, not on every price tick) ──
  const structKey = enabledNew.map(l => `${l.action}${l.optionType}${l.strike}${l.expiry}x${l.lots}`).join('|') + `*${mult}`;
  useEffect(() => {
    if (!enabledNew.length) { setMargin({ inr: null, err: null, loading: false }); return; }
    let alive = true;
    setMargin(m => ({ ...m, loading: true }));
    const t = setTimeout(async () => {
      const { legs: exp, lots } = expandForOrder(legs);
      try {
        const r: any = await optionsService.previewBasket({ underlyingSymbol: base, strategyId: cls.id, legs: exp });
        if (!alive) return;
        const card = r.data?.data?.card;
        setMargin({ inr: card ? Number(card.marginEstimate || 0) * lots * mult : null, err: null, loading: false });
      } catch (e: any) {
        if (!alive) return;
        setMargin({ inr: null, err: e?.response?.data?.error?.message || 'Margin preview unavailable', loading: false });
      }
    }, 900);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structKey, base]);

  const update = (id: string, patch: Partial<WsLeg>) => setLegs(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)));

  /** Live quote for a contract: the viewed expiry's row, else the leg-symbol quotes. */
  const quoteFor = useCallback((t: OptType, strike: number, iso: string) => {
    const row = chain?.chain?.iso === iso ? chain.chain.rows.find(r => r.strike === strike) : undefined;
    return row ? (t === 'CE' ? row.ce : row.pe) : chain?.quotes[deltaSymbol(t, base, strike, iso)];
  }, [chain, base]);

  /** Re-point a leg at a new contract: new symbol, and a MKT leg re-prices from the chain. */
  const pointed = useCallback((n: WsLeg): WsLeg => {
    const exp = chain?.expiries.find(e => e.iso === n.expiry);
    const out = { ...n, symbol: deltaSymbol(n.optionType, base, n.strike, n.expiry) };
    if (exp) out.settleMs = exp.settleMs;
    const q = quoteFor(n.optionType, n.strike, n.expiry);
    if (q?.iv) out.iv = q.iv;
    const mk = markOf(q);
    const ex = execPrice(q, out.action, prefs.fillModel);
    if (out.priceMode === 'MKT' && ex) out.price = ex;
    if (mk) out.mark = mk;
    return out;
  }, [chain, base, quoteFor, prefs.fillModel]);
  const repoint = (l: WsLeg, patch: Partial<WsLeg>) => update(l.id, pointed({ ...l, ...patch }));

  /** What a HELD leg makes right now if it were closed: a long sells at the bid, a short buys
   *  back at the ask (Bid/Ask pricing; Mark pricing uses the mark). Before fees. A leg already
   *  marked CLOSE uses the same close price the payoff books it at. */
  const legPnl = (l: WsLeg): { usd: number; exitPx: number; markUsd: number | null } | null => {
    if (!l.held) return null;
    const q = quoteFor(l.optionType, l.strike, l.expiry);
    const exitPx = l.closing && l.closeMark != null ? l.closeMark : (execPrice(q, l.action === 'SELL' ? 'BUY' : 'SELL', prefs.fillModel) ?? l.mark ?? null);
    if (exitPx == null || !Number.isFinite(exitPx)) return null;
    const sg = l.action === 'BUY' ? 1 : -1;
    return { usd: sg * (exitPx - l.price) * l.lots * cv, exitPx, markUsd: l.mark != null ? sg * (l.mark - l.price) * l.lots * cv : null };
  };
  const anyHeld = legs.some(l => l.held);
  const [panelW, setPanelW] = useState(0);
  const narrow = panelW > 0 && panelW < NARROW_PX;
  const pnlCol = anyHeld && !narrow;
  const grid = narrow ? GRID_NARROW : pnlCol ? GRID_PNL : GRID;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setPanelW(el.clientWidth));
    ro.observe(el);
    setPanelW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const pnlCell = (l: WsLeg, line: boolean) => {
    const pl = legPnl(l);
    if (!pl) return line ? null : <Box sx={{ textAlign: 'right', color: c.faint, fontSize: 11 }}>{l.held ? '—' : ''}</Box>;
    const col = pl.usd > 0 ? c.green : pl.usd < 0 ? c.red : c.text;
    const pct = l.price > 0 ? ((l.action === 'BUY' ? 1 : -1) * (pl.exitPx - l.price) / l.price) * 100 : null;
    const tip = `${l.action === 'SELL' ? 'Sold' : 'Bought'} at $${fmtPx(l.price)} → ${l.closing ? 'closing' : 'close'} at $${fmtPx(pl.exitPx)} (${l.action === 'SELL' ? 'buy back at the ask' : 'sell at the bid'}${prefs.fillModel === 'mark' ? ' — Mark pricing: at the mark' : ''}) × ${l.lots} lot${l.lots > 1 ? 's' : ''}. At the mark: ${pl.markUsd != null ? fmtMoney(pl.markUsd, ccy, { sign: true }) : '—'}. Before fees.`;
    const pctTxt = pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%` : '';
    return (
      <Tooltip disableInteractive title={tip}>
        {line ? (
          <Box data-testid="ws-leg-pnl" sx={{ gridColumn: '1 / -1', textAlign: 'right', fontSize: 10.5, color: c.faint, fontVariantNumeric: 'tabular-nums', cursor: 'help', pt: 0.2 }}>
            P&amp;L if closed now <b style={{ color: col, fontSize: 11.5 }}>{fmtMoney(pl.usd, ccy, { sign: true })}</b>{pctTxt ? ` · ${pctTxt}` : ''}
          </Box>
        ) : (
          <Box data-testid="ws-leg-pnl" sx={{ textAlign: 'right', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', cursor: 'help', minWidth: 0 }}>
            <Box sx={{ fontWeight: 700, color: col, fontSize: 11.5, whiteSpace: 'nowrap' }}>{fmtMoney(pl.usd, ccy, { sign: true })}</Box>
            {pctTxt && <Box sx={{ fontSize: 9.5, color: c.faint, whiteSpace: 'nowrap' }}>{pctTxt}</Box>}
          </Box>
        )}
      </Tooltip>
    );
  };

  const makeLeg = (t: OptType, action: Side, strike: number, expiry: string, lots: number, extra: Partial<WsLeg> = {}): WsLeg =>
    pointed({ id: newLegId(), action, optionType: t, strike, expiry, settleMs: Date.parse(`${expiry}T12:00:00Z`), lots,
      price: 0, priceMode: 'MKT', enabled: true, iv: atmIv || 0.5, symbol: '', ...extra });

  // ── open baskets → held legs ──
  const loadOpen = async (anchor: HTMLElement) => {
    setOpenMenu(anchor);
    try {
      const r: any = await optionsService.getBaskets('EXECUTED');
      const rows = (r.data?.data || []).filter((b: any) => !b.closedAt && Array.isArray(b.legFills) && b.legFills.some((f: any) => String(f.symbol || '').includes(`-${base}-`)));
      setOpenBaskets(rows);
      if (rows.length) {
        optionsService.getBasketsMargin(rows.map((b: any) => b.id))
          .then((mr: any) => setMenuMargin(mr.data?.data || {})).catch(() => {});
      }
    } catch { setOpenBaskets([]); }
  };
  const heldFrom = (b: any): WsLeg[] => {
    const out: WsLeg[] = [];
    for (const f of b.legFills || []) {
      const p = parseDeltaSymbol(f.symbol);
      if (!p || p.base !== base || f.fillPrice == null || !f.filled) continue;
      const exp = chain?.expiries.find(e => e.iso === p.expiry);
      out.push({
        id: newLegId(), action: f.action, optionType: p.optionType, strike: p.strike, expiry: p.expiry,
        settleMs: exp?.settleMs ?? Date.parse(`${p.expiry}T12:00:00Z`), lots: Number(f.quantity) || 1,
        price: Number(f.fillPrice), priceMode: 'LMT', enabled: true, iv: atmIv || 0.5, symbol: f.symbol,
        held: true, heldBasketId: b.id,
      });
    }
    return out;
  };
  const addHeld = (b: any, alongside = false) => {
    setOpenMenu(null);
    const h = heldFrom(b);
    if (!h.length) { toast.error('That basket has no filled legs on the live chain'); return; }
    // Choosing a position shows THAT position: legs of other loaded positions (and their
    // pending rolls) are dropped. Draft legs stay — they may be meant for this position.
    setLegs(prev => [
      ...prev.filter(l => (alongside
        ? (l.heldBasketId !== b.id && l.rollBasketId !== b.id)
        : (l.heldBasketId == null && l.rollBasketId == null))),
      ...h,
    ]);
    setTarget('add');
    toast.success(alongside ? `Added #${b.id} ${b.strategy} alongside` : `Showing #${b.id} ${b.strategy} — its legs are analysed, not re-ordered`);
  };
  const reloadHeld = async (id: number) => {
    const r: any = await optionsService.getBasket(id);
    const b = r.data?.data?.basket;  // GET /baskets/:id → { basket, gates }
    if (!b || b.closedAt) { setLegs(prev => prev.filter(l => l.heldBasketId !== id && l.rollBasketId !== id)); return; }
    setLegs(prev => [...prev.filter(l => l.heldBasketId !== id && l.rollBasketId !== id), ...heldFrom(b)]);
  };

  // ── roll: mark a held leg CLOSE and add its replacement ──
  // Adjustments follow the basket's own mode; LIVE ones are confirmed first (see liveAsk).
  const paperOnly = (_id: number) => true;
  const isLiveBasket = (id: number) => {
    const b = held[id];
    if (b) return b.tradingMode !== 'PAPER';
    return openBaskets.some(x => x.id === id && x.tradingMode !== 'PAPER');
  };
  const toggleRoll = (l: WsLeg, toStrike?: number) => {
    if (!l.held || l.heldBasketId == null || !paperOnly(l.heldBasketId)) return;
    setLegs(prev => {
      const without = prev.filter(x => x.rollOf !== l.id);
      if (l.closing && toStrike == null) return without.map(x => (x.id === l.id ? { ...x, closing: false, closeMark: undefined } : x));
      const repl = makeLeg(l.optionType, l.action, toStrike ?? l.strike, l.expiry, l.lots, { rollBasketId: l.heldBasketId, rollOf: l.id });
      return [...without.map(x => (x.id === l.id ? { ...x, closing: true } : x)), repl];
    });
  };
  /** Close a held leg WITHOUT a replacement (e.g. a leg added by mistake). Sent as a
   *  close-only adjustment of its basket; closing every leg is a basket close instead. */
  const toggleClose = (l: WsLeg) => {
    if (!l.held || l.heldBasketId == null) return;
    setLegs(prev => {
      const without = prev.filter(x => x.rollOf !== l.id);
      if (l.closing) return without.map(x => (x.id === l.id ? { ...x, closing: false, closeMark: undefined } : x));
      return without.map(x => (x.id === l.id ? { ...x, closing: true } : x));
    });
  };
  /** What a close-only adjustment would leave behind: shorts whose protective long goes. */
  const nakedAfter = (id: number): string[] => {
    const remain = legs.filter(l => l.heldBasketId === id && l.enabled && !l.closing)
      .concat(legs.filter(l => l.rollBasketId === id && l.enabled));
    const out: string[] = [];
    for (const t of ['CE', 'PE'] as const) {
      const shorts = remain.filter(l => l.optionType === t && l.action === 'SELL').reduce((a, l) => a + l.lots, 0);
      const longs = remain.filter(l => l.optionType === t && l.action === 'BUY').reduce((a, l) => a + l.lots, 0);
      const hadLong = legs.some(l => l.heldBasketId === id && l.optionType === t && l.action === 'BUY');
      if (shorts > longs && hadLong) out.push(t === 'CE' ? 'call' : 'put');
    }
    return out;
  };

  const suggestRoll = (id: number) => {
    if (!paperOnly(id)) return;
    const mine = legs.filter(l => l.heldBasketId === id && l.enabled);
    const pair = shortPair(mine.map(l => ({ ...l, closing: false })));
    if (!pair) { toast.error('The roll rule needs exactly one short call and one short put'); return; }
    if (pair.straddle) { toast('Straddle reached — the playbook stops adjusting here', { icon: '⛔' }); return; }
    const cheap = pair.cheap === 'CE' ? pair.ce : pair.pe, dear = pair.cheap === 'CE' ? pair.pe : pair.ce;
    if (chain?.chain?.iso !== cheap.expiry) { viewExpiry(cheap.expiry); toast(`Showing ${expLabel(cheap.expiry)} — press Suggest roll again`, { icon: '↻' }); return; }
    const target = dear.mark ?? dear.price;
    const m = matchStrike(cheap.optionType, sideQuotes(chain.chain.rows, cheap.optionType), target, dear.strike);
    if (!m) { toast.error('No quoted strike to roll to'); return; }
    if (m.strike === cheap.strike) { toast('Already the closest premium match — nothing to roll', { icon: '✓' }); return; }
    toggleRoll(cheap, m.strike);
    toast.success(`Roll ${cheap.optionType} ${fmtStrike(cheap.strike)} → ${fmtStrike(m.strike)} ($${fmtPx(m.price)} vs $${fmtPx(target)})${m.strike === dear.strike ? ' — this makes it a straddle (last adjustment)' : ''}`);
  };

  // ── execute a pending adjustment (PAPER) ──
  const pendingIds = Array.from(new Set(legs.filter(l => (l.closing && l.heldBasketId != null) || l.rollBasketId != null)
    .map(l => (l.rollBasketId ?? l.heldBasketId) as number)));
  const executeAdj = async (id: number, confirmed = false) => {
    if (!paperOnly(id)) return;
    if (isLiveBasket(id) && !confirmed) { setLiveAsk({ id, kind: 'adjust' }); return; }
    const closeLegs = legs.filter(l => l.heldBasketId === id && l.closing).map(l => ({ symbol: l.symbol, action: l.action, quantity: l.lots }));
    const openLegs = legs.filter(l => l.rollBasketId === id && l.enabled).map(l => ({ symbol: l.symbol, action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry, quantity: l.lots }));
    if (!closeLegs.length && !openLegs.length) return;
    if (!openLegs.length && closeLegs.length === legs.filter(l => l.heldBasketId === id).length) { setCloseDlg(id); return; }
    setBusy(id);
    try {
      const r: any = await optionsService.adjustBasket(id, { closeLegs, openLegs });
      const booked = Number(r.data?.data?.bookedPnl || 0);
      toast.success(`${isLiveBasket(id) ? 'LIVE' : 'PAPER'} adjustment done on #${id} — booked ≈ ₹${Math.round(booked).toLocaleString('en-IN')}`);
      await reloadHeld(id);
      refreshHeld();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Adjustment failed');
    } finally { setBusy(null); }
  };
  const cancelAdj = (id: number) => setLegs(prev => prev.filter(l => l.rollBasketId !== id).map(l => (l.heldBasketId === id ? { ...l, closing: false, closeMark: undefined } : l)));

  const closeBasketNow = async (id: number) => {
    setCloseDlg(null);
    if (!paperOnly(id)) return;
    setBusy(id);
    try {
      await optionsService.closeBasket(id);
      toast.success(`${isLiveBasket(id) ? 'LIVE basket #' + id + ' closed with market orders' : 'PAPER basket #' + id + ' squared off at the live marks'}`);
      setLegs(prev => prev.filter(l => l.heldBasketId !== id && l.rollBasketId !== id));
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Close failed');
    } finally { setBusy(null); }
  };

  // ── entry helpers ──
  const matchPremiums = () => {
    const pair = shortPair(enabledNew);
    if (!pair) return;
    const cheap = pair.cheap === 'CE' ? pair.ce : pair.pe, dear = pair.cheap === 'CE' ? pair.pe : pair.ce;
    if (chain?.chain?.iso !== cheap.expiry) { viewExpiry(cheap.expiry); toast(`Showing ${expLabel(cheap.expiry)} — press Match premiums again`, { icon: '↻' }); return; }
    const m = matchStrike(cheap.optionType, sideQuotes(chain.chain.rows, cheap.optionType), dear.mark ?? dear.price, dear.strike);
    if (!m || m.strike === cheap.strike) { toast('Already the closest premium match', { icon: '✓' }); return; }
    repoint(cheap, { strike: m.strike });
  };
  const addWings = () => {
    // New shorts AND the shorts of the open position on screen (a held strangle → condor).
    const pool = legs.filter(l => l.enabled && !l.closing);
    const sells = pool.filter(l => l.action === 'SELL');
    const add: WsLeg[] = [];
    const noStrike: string[] = [];
    for (const s of sells) {
      const covered = [...pool, ...add].some(b => b.action === 'BUY' && b.optionType === s.optionType && b.expiry === s.expiry && (s.optionType === 'CE' ? b.strike > s.strike : b.strike < s.strike));
      if (covered) continue;
      const k = stepStrike(strikesFor(s.expiry), s.strike, s.optionType === 'CE' ? 2 : -2);
      if (k !== s.strike) add.push(makeLeg(s.optionType, 'BUY', k, s.expiry, s.lots));
      else noStrike.push(`${fmtStrike(s.strike)} ${s.optionType}`);
    }
    if (noStrike.length) toast(`No listed strike beyond ${noStrike.join(' / ')} on that expiry, so that short gets no wing`, { icon: 'ℹ️', duration: 6000 });
    if (!add.length) { if (!noStrike.length) toast('Every short already has a wing', { icon: '✓' }); return; }
    setLegs(prev => [...prev, ...add]);
    setCtlOpen(true);
  };

  // ── place a new basket ──
  const newStats = useMemo(() => computeStats(enabledNew, spot, cv, atmIv, Date.now()), [enabledNew, spot, cv, atmIv]);
  const newNetUsd = netPremium(enabledNew, cv) * mult;
  // Spread cost of the analysis prices vs Delta's mark (touch model: what crossing the
  // bid/ask costs at the top of the book).
  const touchSlipUsd = enabledNew.reduce((s, l) => s + Math.abs(l.price - (l.mark ?? l.price)) * l.lots * mult * cv, 0);
  // Confirm dialog: the order book walked for each leg's FULL size — the paper engine fills
  // at exactly this price, so the plan below is sized off the credit you'll actually get.
  const bookOk = enabledNew.length > 0 && enabledNew.every(l => book.rows[l.id]?.entry);
  const bookNetUsd = bookOk ? enabledNew.reduce((s, l) => s + (l.action === 'SELL' ? 1 : -1) * (book.rows[l.id].entry as any).price * l.lots * mult * cv, 0) : null;
  const bookEntrySlipUsd = bookOk ? enabledNew.reduce((s, l) => s + Math.abs((book.rows[l.id].entry as any).price - (l.mark ?? l.price)) * l.lots * mult * cv, 0) : null;
  const exitSlipUsd = enabledNew.every(l => book.rows[l.id]?.exit)
    ? enabledNew.reduce((s, l) => s + Math.abs((book.rows[l.id].exit as any).price - (l.mark ?? l.price)) * l.lots * mult * cv, 0) : null;
  const creditInr = (bookNetUsd ?? newNetUsd) * USD_TO_INR;
  const entryFeeUsd = enabledNew.reduce((s, l) => s + feeUsd(l.price, spot, l.lots * mult, cv, prefs.feeRatePct), 0);
  const roundTripFeeInr = entryFeeUsd * 2 * USD_TO_INR;   // ≈ exit at a similar premium
  const exitSlipInr = (exitSlipUsd ?? 0) * USD_TO_INR;
  // NET levels (2026-11-03): the monitor measures the target / stop after the exit slippage and
  // both sides' fees, so nothing is added here — +₹tp is what the basket books.
  const tpInr = Math.round(creditInr * prefs.tpPctOfCredit / 100);
  const slInr = Math.round(creditInr * prefs.slPctOfCredit / 100);
  const fixedOk = creditInr > 0 && tpInr > 0 && slInr > 0;
  const bookKey = confirm ? enabledNew.map(l => `${l.symbol}:${l.action}:${l.lots * mult}`).join('|') : '';
  useEffect(() => {
    if (!bookKey) return;
    let alive = true;
    setBook({ loading: true, rows: {} });
    const both = enabledNew.length <= 4;
    const req = enabledNew.flatMap(l => {
      const qty = l.lots * mult, ent: 'buy' | 'sell' = l.action === 'SELL' ? 'sell' : 'buy';
      return both ? [{ symbol: l.symbol, side: ent, qty }, { symbol: l.symbol, side: (ent === 'sell' ? 'buy' : 'sell') as 'buy' | 'sell', qty }] : [{ symbol: l.symbol, side: ent, qty }];
    });
    fetchBookFills(req).then(rows => {
      if (!alive) return;
      const m: Record<string, { entry: BookFillRow['fill']; exit: BookFillRow['fill']; withinCap: number | null; capPct: number | null }> = {};
      for (const l of enabledNew) {
        const ent = l.action === 'SELL' ? 'sell' : 'buy';
        const er = rows.find(r => r.symbol === l.symbol && r.side === ent);
        m[l.id] = {
          entry: er?.fill ?? null,
          exit: rows.find(r => r.symbol === l.symbol && r.side !== ent)?.fill ?? null,
          withinCap: er?.withinCap ?? null, capPct: er?.capPct ?? null,
        };
      }
      setBook({ loading: false, rows: m });
    }).catch(() => { if (alive) setBook({ loading: false, rows: {} }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookKey]);
  const newPair = shortPair(enabledNew);
  const thinBook = enabledNew.map(l => {
    const q = quoteFor(l.optionType, l.strike, l.expiry);
    const size = l.action === 'SELL' ? q?.bidSize : q?.askSize;
    return size != null && size > 0 && l.lots * mult > size ? { l, size } : null;
  }).filter(Boolean) as Array<{ l: WsLeg; size: number }>;

  const addToId = heldIds.length === 1 && target === 'add' ? heldIds[0] : null;
  const addToLive = addToId != null && isLiveBasket(addToId);
  const effMode: 'LIVE' | 'PAPER' = addToId != null ? (addToLive ? 'LIVE' : 'PAPER') : orderMode;
  const place = async () => {
    if (addToId != null) {
      // Add the new legs to the open position (open-only adjustment of that basket).
      const openLegs = enabledNew.map(l => ({ symbol: l.symbol, action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry, quantity: l.lots * mult }));
      if (!openLegs.length) return;
      setPlacing(true);
      try {
        await optionsService.adjustBasket(addToId, { closeLegs: [], openLegs });
        toast.success(`${effMode} — added ${openLegs.length} leg${openLegs.length > 1 ? 's' : ''} to #${addToId}`);
        setConfirm(false);
        setLegs(prev => prev.filter(l => !(isNewLeg(l) && l.enabled)));
        await reloadHeld(addToId);
        refreshHeld();
      } catch (e: any) {
        toast.error(e?.response?.data?.error?.message || e?.message || 'Could not add the legs');
      } finally { setPlacing(false); }
      return;
    }
    const { legs: exp, lots } = expandForOrder(legs);
    if (!exp.length) return;
    const live = orderMode === 'LIVE';
    setPlacing(true);
    try {
      const fixed = exitMode === 'FIXED' && fixedOk;
      const r: any = await optionsService.createBasketFromLegs({
        underlyingSymbol: base, strategyId: cls.id, legs: exp, lots: lots * mult, mode: live ? 'LIVE' : 'PAPER', strategyName: cls.name,
        ...(live ? {} : { paperFill: 'BOOK' }),
        ...(fixed ? { exitMode: 'FIXED' as const, tpAmountInr: tpInr, slAmountInr: slInr } : {}),
        ...(newPair && ratioAlert ? { ratioAlertX: prefs.ratioX } : {}),
      });
      const id = r.data?.data?.basketId;
      if (!id) throw new Error('Basket was not created');
      await optionsService.approveBasket(id);
      toast.success(`${live ? 'LIVE' : 'PAPER'} ${cls.name} placed (#${id})${fixed ? ` — TP +₹${tpInr.toLocaleString('en-IN')} / SL −₹${slInr.toLocaleString('en-IN')} on total P&L` : ''}`);
      setConfirm(false);
      // The placed basket's REAL fills replace the draft legs.
      setLegs(prev => prev.filter(l => !(isNewLeg(l) && l.enabled)));
      reloadHeld(id).catch(() => {});
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || (live ? 'Live order failed' : 'Paper order failed'));
    } finally {
      setPlacing(false);
    }
  };

  const stepBtn = (onClick: () => void, label: string, disabled = false) => (
    <Box component="button" type="button" disabled={disabled} onClick={onClick}
      sx={{ all: 'unset', cursor: disabled ? 'default' : 'pointer', width: 18, textAlign: 'center', color: disabled ? c.faint : c.dim, fontWeight: 700, borderRadius: 0.5, '&:hover': disabled ? {} : { bgcolor: c.hover, color: c.text } }}>{label}</Box>
  );
  const box = (children: React.ReactNode, sx: any = {}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${c.border}`, borderRadius: 0.8, height: 26, px: 0.3, bgcolor: c.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', ...sx }}>{children}</Box>
  );
  const linkBtn = (label: string, onClick: () => void, color = c.blue, tip?: string) => (
    <Tooltip title={tip || ''} disableInteractive>
      <Box component="button" type="button" onClick={onClick} sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</Box>
    </Tooltip>
  );
  const iconBtn = (title: string, icon: React.ReactNode, onClick: () => void, active = false) => (
    <Tooltip title={title} disableInteractive>
      <IconButton size="small" sx={{ p: 0.2, color: active ? c.amber : c.faint }} onClick={onClick}>{icon}</IconButton>
    </Tooltip>
  );

  const prem = newNetUsd;
  const premLabel = prem >= 0 ? 'Get' : 'Pay';

  // Credit or debit from the card as it would build on the viewed chain right now.
  const costOf = useMemo(() => {
    const m: Record<string, Cost> = {};
    for (const rm of READY_MADE) {
      let v: Cost | null = null;
      if (chain?.chain) {
        const b = buildReadyMade(rm, chain, atmIv || 0.5);
        if (b && b.length) {
          const net = builtNetPremium(b.map(l => ({ action: l.action, price: l.price, lots: l.lots } as unknown as TplBuiltLeg)));
          v = net >= 0 ? 'Credit' : 'Debit';
        }
      }
      m[rm.id] = v || STATIC_COST[rm.id] || 'Debit';
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain?.chain?.iso, chain?.spot ? Math.round(chain.spot / 50) : 0, READY_MADE.length]);
  const rmList = READY_MADE.filter(r => (!outlooks.length || outlooks.indexOf(r.outlook) >= 0) && (!costs.length || costs.indexOf(costOf[r.id]) >= 0));
  const toggleIn = <T,>(arr: T[], v: T): T[] => (arr.indexOf(v) >= 0 ? arr.filter(x => x !== v) : [...arr, v]);
  const applyReadyMade = (id: string) => {
    if (!chain) return;
    const rm = READY_MADE.find(r => r.id === id)!;
    const built = buildReadyMade(rm, chain, atmIv || 0.5);
    if (!built) { toast.error(`${rm.name}: the ${chain.chain ? expLabel(chain.chain.iso) : ''} chain can't host it (strikes/quotes missing)`); return; }
    setLegs(prev => [...prev.filter(l => !isNewLeg(l)), ...built]);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.7, borderBottom: `1px solid ${c.border}`, flexWrap: 'wrap' }}>
        <Box sx={{ fontSize: 11, letterSpacing: 0.8, fontWeight: 700, color: c.dim }}>POSITIONS</Box>
        {newLegs.length > 0 && (
          <Box sx={{ fontSize: 11.5, fontWeight: 700, px: 0.8, py: 0.1, border: `1px solid ${c.border}`, borderRadius: 0.8 }}>
            {cls.name} · {enabledNew.length}/{newLegs.length}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontSize: 10.5, color: c.faint }}>1 lot = {cv} {base}</Box>
        {onChartBasket && legs.some(l => l.enabled && !l.closing) && (
          <Tooltip title="Chart the combined premium of these legs as candles in the strip">
            <Box component="button" type="button" data-testid="ws-chart-basket" onClick={onChartBasket}
              sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 11, color: c.amber, fontWeight: 700 }}>
              <ShowChartIcon sx={{ fontSize: 14 }} /> Basket chart
            </Box>
          </Tooltip>
        )}
        <Box component="button" type="button" onClick={e => loadOpen(e.currentTarget)}
          sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, color: c.blue, fontWeight: 600 }}>Open positions ▾</Box>
        {newLegs.some(l => l.priceMode === 'LMT') && linkBtn('Reset prices', () => setLegs(prev => prev.map(l => (isNewLeg(l) ? { ...l, priceMode: 'MKT' } : l))), c.amber)}
        {legs.length > 0 && (
          <Box component="button" type="button" onClick={() => setLegs(() => [])}
            sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, px: 0.8, py: 0.1, border: `1px solid ${c.border}`, borderRadius: 0.8, color: c.dim }}>Clear</Box>
        )}
        <Menu anchorEl={openMenu} open={!!openMenu} onClose={() => setOpenMenu(null)}>
          {openBaskets.length === 0 && <MenuItem disabled sx={{ fontSize: 12 }}>No open {base} baskets</MenuItem>}
          {openBaskets.map(b => (
            <MenuItem key={b.id} onClick={() => addHeld(b)} sx={{ fontSize: 12, gap: 1 }} selected={heldIds.includes(b.id)}>
              <b>#{b.id}</b> {b.strategy}
              <Box component="span" sx={{ fontSize: 10, px: 0.5, borderRadius: 0.5, bgcolor: b.tradingMode === 'PAPER' ? c.amberBg : c.redBg, color: b.tradingMode === 'PAPER' ? c.amber : c.red }}>{b.tradingMode}</Box>
              {b.exitMode === 'FIXED' && <Box component="span" sx={{ fontSize: 10, color: c.dim }}>fixed ₹</Box>}
              {b.live?.unrealizedPnl != null && <Box component="span" sx={{ color: b.live.unrealizedPnl >= 0 ? c.green : c.red }}>₹{Math.round(b.live.unrealizedPnl).toLocaleString('en-IN')}</Box>}
              {menuMargin[b.id]?.marginInr != null && (
                <Box component="span" data-testid="ws-menu-margin" sx={{ fontSize: 10.5, color: c.dim }}>
                  margin ₹{Math.round(menuMargin[b.id].marginInr as number).toLocaleString('en-IN')}{menuMargin[b.id].source === 'BROKER' ? '' : ' est.'}
                </Box>
              )}
              <Box sx={{ flex: 1 }} />
              {heldIds.length > 0 && !heldIds.includes(b.id) && (
                <Tooltip title="Add alongside the position on screen (combined analysis)">
                  <Box component="span" role="button" onClick={(e: any) => { e.stopPropagation(); addHeld(b, true); }}
                    sx={{ px: 0.6, borderRadius: 0.5, border: `1px solid ${c.border}`, color: c.dim, fontWeight: 700, '&:hover': { color: c.text } }}>+</Box>
                </Tooltip>
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto' }}>
        <PlaybookCard legs={legs} held={held} margin={heldMargin} ratioX={prefs.ratioX} ccy={ccy} c={c}
          onMatch={matchPremiums} onSuggestRoll={suggestRoll} onExitRules={setExitDlg} onCloseBasket={setCloseDlg} />

        {legs.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: c.dim, fontSize: 12.5, py: 3, px: 2 }}>
            <Box sx={{ fontWeight: 700, color: c.text, mb: 0.5 }}>No legs yet</Box>
            Click a <b style={{ color: c.green }}>Bid</b> to sell or an <b style={{ color: c.red }}>Ask</b> to buy — or hover a strike and press{' '}
            <Box component="span" sx={{ px: 0.5, borderRadius: 0.5, bgcolor: c.red, color: '#fff', fontWeight: 700 }}>S</Box> or{' '}
            <Box component="span" sx={{ px: 0.5, borderRadius: 0.5, bgcolor: c.green, color: '#fff', fontWeight: 700 }}>B</Box>
            {' '}— or start from a ready-made strategy below.
          </Box>
        ) : (
          <Box sx={{ px: 0.8, pt: 0.6 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: grid, columnGap: narrow ? 0.3 : 0.5, alignItems: 'center', fontSize: 10, color: c.faint, pb: 0.4, px: 0.2 }}>
              <span /><span>B/S</span><span style={{ textAlign: 'center' }}>Strike</span><span>Type</span><span style={{ textAlign: 'center' }}>Lots</span><span style={{ textAlign: 'right' }}>Price</span>
              {pnlCol && <Tooltip title="What each held leg makes now if closed — a long sold at the bid, a short bought back at the ask (Mark pricing: at the mark). Before fees; the position's net Open / Total P&L above includes them."><span style={{ textAlign: 'right', cursor: 'help' }}>P&amp;L</span></Tooltip>}
              <span>Expiry</span><span />
            </Box>
            {legs.map(l => {
              const ks = strikesFor(l.expiry);
              const ro = !!l.held;
              const isRoll = l.rollBasketId != null;
              return (
                <Box key={l.id} data-ws-leg={l.held ? `held-${l.heldBasketId}` : isRoll ? "roll" : "new"} sx={{
                  display: 'grid', gridTemplateColumns: grid, columnGap: narrow ? 0.3 : 0.5, alignItems: 'center', py: 0.4, px: 0.2, borderBottom: `1px solid ${c.border}`,
                  opacity: l.enabled ? (l.closing ? 0.6 : 1) : 0.45, fontSize: 12,
                  borderLeft: isRoll ? `2px solid ${c.amber}` : l.closing ? `2px solid ${c.red}` : '2px solid transparent',
                  bgcolor: isRoll ? c.amberBg : 'transparent',
                }}>
                  <input type="checkbox" checked={l.enabled} disabled={l.closing} onChange={e => update(l.id, { enabled: e.target.checked })} style={{ accentColor: c.amber, margin: 0 }} />
                  <Box component="button" type="button" disabled={ro || isRoll} onClick={() => update(l.id, { action: l.action === 'BUY' ? 'SELL' : 'BUY' })}
                    sx={{ all: 'unset', cursor: ro || isRoll ? 'default' : 'pointer', textAlign: 'center', fontWeight: 800, fontSize: 11, borderRadius: 0.6, height: 22, lineHeight: '22px',
                      bgcolor: l.action === 'BUY' ? c.greenBg : c.redBg, color: l.action === 'BUY' ? c.green : c.red }}>{l.action[0]}</Box>
                  {box(<>
                    {stepBtn(() => repoint(l, { strike: ks[Math.max(0, ks.indexOf(l.strike) - 1)] ?? l.strike }), '−', ro)}
                    <Box component="select" disabled={ro} value={l.strike} onChange={(e: any) => repoint(l, { strike: Number(e.target.value) })}
                      sx={{ all: 'unset', flex: 1, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums', cursor: ro ? 'default' : 'pointer', textDecoration: l.closing ? 'line-through' : 'none', '& option': { color: '#000' } }}>
                      {(ks.includes(l.strike) ? ks : [...ks, l.strike].sort((a, b) => a - b)).map(k => <option key={k} value={k}>{fmtStrike(k)}</option>)}
                    </Box>
                    {stepBtn(() => repoint(l, { strike: ks[Math.min(ks.length - 1, ks.indexOf(l.strike) + 1)] ?? l.strike }), '+', ro)}
                  </>)}
                  <Box component="button" type="button" disabled={ro || isRoll} onClick={() => repoint(l, { optionType: l.optionType === 'CE' ? 'PE' : 'CE' })}
                    sx={{ all: 'unset', cursor: ro || isRoll ? 'default' : 'pointer', textAlign: 'center', fontWeight: 700, fontSize: 11, color: l.optionType === 'CE' ? c.call : c.put }}>{l.optionType}</Box>
                  {box(
                    <Tooltip title={`${l.lots} lot${l.lots > 1 ? 's' : ''} = ${+(l.lots * cv).toFixed(4)} ${base}`} disableInteractive>
                      <Box component="input" type="number" min={1} max={MAX_LOTS} disabled={ro || isRoll} value={l.lots}
                        onChange={(e: any) => { const v = Math.floor(Number(e.target.value)); if (v >= 1) update(l.id, { lots: Math.min(MAX_LOTS, v) }); }}
                        sx={{ all: 'unset', width: '100%', minWidth: 0, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums', ...NO_SPIN }} />
                    </Tooltip>,
                  )}
                  {box(<>
                    <Box component="input" type="number" disabled={ro || l.priceMode === 'MKT'} value={l.priceMode === 'MKT' || ro ? +l.price.toFixed(l.price >= 100 ? 1 : 2) : l.price}
                      onChange={(e: any) => { const v = Number(e.target.value); if (v >= 0) update(l.id, { price: v }); }}
                      sx={{ all: 'unset', width: '100%', minWidth: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: l.priceMode === 'MKT' ? c.dim : c.text, ...NO_SPIN }} />
                    {ro ? <Tooltip title={l.closing ? `CLOSE at the live mark ≈ $${fmtPx(l.closeMark ?? l.mark ?? null)} (fill $${fmtPx(l.price)})` : `Held in basket #${l.heldBasketId} — fill $${fmtPx(l.price)}, mark $${fmtPx(l.mark ?? null)}`}>
                      {l.closing ? <Box component="span" sx={{ fontSize: 8.5, fontWeight: 800, ml: 0.3, color: c.red }}>{narrow ? '×' : 'CLOSE'}</Box> : narrow ? <span /> : <LockIcon sx={{ fontSize: 12, color: c.faint, ml: 0.3 }} />}
                    </Tooltip> : (
                      <Tooltip title={l.priceMode === 'MKT' ? 'Following the live mark — click to set your own price (analysis only; paper fills at the mark)' : 'Your price — click to follow the live mark'}>
                        <Box component="button" type="button" onClick={() => update(l.id, { priceMode: l.priceMode === 'MKT' ? 'LMT' : 'MKT' })}
                          sx={{ all: 'unset', cursor: 'pointer', fontSize: 8.5, fontWeight: 700, ml: 0.3, color: l.priceMode === 'MKT' ? c.faint : c.amber }}>{isRoll ? 'NEW' : l.priceMode}</Box>
                      </Tooltip>
                    )}
                  </>)}
                  {pnlCol && pnlCell(l, false)}
                  <Box component="select" disabled={ro || isRoll} value={l.expiry}
                    onChange={(e: any) => { const iso = e.target.value; const nk = strikesFor(iso); repoint(l, { expiry: iso, strike: nk.includes(l.strike) ? l.strike : (nk.reduce((p, q) => (Math.abs(q - l.strike) < Math.abs(p - l.strike) ? q : p), nk[0] ?? l.strike)) }); }}
                    sx={{ all: 'unset', fontSize: 10.5, color: c.dim, cursor: ro || isRoll ? 'default' : 'pointer', whiteSpace: 'nowrap', '& option': { color: '#000' } }}>
                    {(chain?.expiries.some(e => e.iso === l.expiry) ? chain!.expiries : [{ iso: l.expiry }, ...(chain?.expiries || [])]).map(e => <option key={e.iso} value={e.iso}>{expLabel(e.iso)}</option>)}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {!(narrow && ro) && iconBtn('Chart this contract in the strip', <ShowChartIcon sx={{ fontSize: 14 }} />, () => onChartContract(l.symbol))}
                    {ro && (() => {
                      const rolling = !!l.closing && legs.some(x => x.rollOf === l.id);
                      const closingOnly = !!l.closing && !rolling;
                      return <>
                        {!closingOnly && iconBtn(rolling ? 'Cancel this roll' : 'Roll: close this leg and open a replacement (sent as an adjustment of its basket)', <AutorenewIcon sx={{ fontSize: 14 }} />, () => toggleRoll(l), rolling)}
                        {!rolling && (
                          <Tooltip title={closingOnly ? 'Keep this leg (cancel the close)' : 'Preview closing this leg: the payoff shows the position without it (dashed = now). Mark several legs to see them closed together. Nothing is sent until you press Close below.'} disableInteractive>
                            <IconButton size="small" data-testid="ws-close-leg" onClick={() => toggleClose(l)} sx={{ p: 0.2, color: closingOnly ? c.red : c.faint, '&:hover': { color: c.red } }}>
                              <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>;
                    })()}
                    {!ro && iconBtn(isRoll ? 'Drop this replacement leg' : 'Remove leg', <CloseIcon sx={{ fontSize: 14 }} />, () => setLegs(prev => prev.filter(x => x.id !== l.id)))}
                  </Box>
                  {narrow && l.held && pnlCell(l, true)}
                </Box>
              );
            })}

            {/* pending adjustments */}
            {pendingIds.map(id => {
              const closes = legs.filter(l => l.heldBasketId === id && l.closing).length;
              const opens = legs.filter(l => l.rollBasketId === id && l.enabled).length;
              const allClose = !opens && closes > 0 && closes === legs.filter(l => l.heldBasketId === id).length;
              const naked = nakedAfter(id);
              return (
                <Box key={`adj${id}`} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.8, mt: 0.8, p: 0.8, borderRadius: 1, border: `1px solid ${c.amber}`, bgcolor: c.amberBg, fontSize: 12 }}>
                  <Tooltip title="The payoff graph, its figures and the chart strip show the position AFTER this change (closed legs booked at the price closing would get). The dashed grey curve in the payoff is the position as it is now.">
                    <Box sx={{ flex: '1 1 160px' }}><b>{opens ? 'Adjust' : 'Close legs of'} #{id}</b> · close {closes} · open {opens}</Box>
                  </Tooltip>
                  <Box data-testid="ws-close-preview-note" sx={{ flex: '1 1 100%', fontSize: 11, color: c.dim }}>
                    Preview — the payoff shows the position after this {opens ? 'adjustment' : `close`} (dashed = as it is now). Nothing is sent until you press the button.
                  </Box>
                  {naked.length > 0 && (
                    <Box data-testid="ws-naked-warn" sx={{ flex: '1 1 100%', color: c.red, fontSize: 11.5 }}>
                      This leaves the short {naked.join(' and ')} without its protective long: the loss is no longer capped and Delta will ask for more margin.
                    </Box>
                  )}
                  <Button size="small" onClick={() => cancelAdj(id)} disabled={busy === id}>Cancel</Button>
                  <Button size="small" variant="contained" color="warning" onClick={() => executeAdj(id)} disabled={busy === id || (!closes && !opens)}>
                    {busy === id ? <CircularProgress size={14} /> : `${allClose ? 'Close basket' : opens ? 'Execute adjustment' : `Close ${closes} leg${closes > 1 ? 's' : ''}`} (${isLiveBasket(id) ? 'LIVE' : 'PAPER'})`}
                  </Button>
                </Box>
              );
            })}

            {/* "+ Add wings" for the open position on screen (no draft legs yet) */}
            {newLegs.length === 0 && heldIds.length === 1 && legs.some(l => l.held && l.enabled && !l.closing && l.action === 'SELL') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.6, fontSize: 11.5, color: c.dim }}>
                <Box sx={{ flex: 1 }}>Hedge #{heldIds[0]}: buy wings beyond its shorts, then add them to the position</Box>
                {linkBtn('+ Add wings', addWings, c.blue, 'Buy a protective wing two strikes beyond every uncovered short of this position (strangle → iron condor). The new legs can then be added to the same basket.')}
              </Box>
            )}

            {/* strike controls (new legs) */}
            {newLegs.length > 0 && (
              <Box sx={{ mt: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="button" type="button" onClick={() => setCtlOpen(o => !o)}
                    sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.6, flex: 1, fontSize: 11.5, color: c.text, fontWeight: 600, py: 0.4 }}>
                    Strike controls <Box component="span" sx={{ color: c.faint, fontWeight: 400 }}>Shift · Width · Hedge</Box>
                    <Box sx={{ flex: 1 }} />{ctlOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: c.faint }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: c.faint }} />}
                  </Box>
                  {legs.some(l => l.enabled && !l.closing && l.action === 'SELL') && linkBtn('+ Add wings', addWings, c.blue, 'Buy a protective wing two strikes beyond every uncovered short (new or held: strangle → iron condor). Then use Hedge to set the distance.')}
                </Box>
                {ctlOpen && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 1, pb: 0.6 }}>
                    {[
                      ['Shift', 'one strike', (d: number) => setLegs(prev => shiftLegs(prev, strikesFor, d)), 'Move every new leg one listed strike', true],
                      ['Width', readout.width != null ? fmtStrike(readout.width) : '—', (d: number) => setLegs(prev => widenLegs(prev, strikesFor, d)), 'Outer span: legs above the centre move up, below move down', newLegs.length >= 2],
                      ['Hedge', readout.hedge != null ? fmtStrike(Math.round(readout.hedge)) : '—', (d: number) => setLegs(prev => hedgeLegs(prev, strikesFor, d)), 'Wing distance: BUY legs move away from / toward their nearest SELL leg', readout.hedge != null],
                    ].map(([label, val, fn, tip, ok]: any) => (
                      <Tooltip key={label} title={tip} enterDelay={500}>
                        <Box>
                          <Box sx={{ fontSize: 10, color: c.faint, mb: 0.3 }}>{label}</Box>
                          {box(<>{stepBtn(() => fn(-1), '−', !ok)}<Box sx={{ flex: 1, textAlign: 'center', fontSize: 11.5, fontWeight: 700 }}>{val}</Box>{stepBtn(() => fn(1), '+', !ok)}</>)}
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* totals + place */}
        {enabledNew.length > 0 && (
          <Box sx={{ px: 1.2, pt: 0.8, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, flexWrap: 'wrap' }}>
              <Box sx={{ color: c.dim }}>Multiplier</Box>
              {box(<>{stepBtn(() => setMult(m => Math.max(1, m - 1)), '−', mult <= 1)}<Box sx={{ width: 34, textAlign: 'center', fontWeight: 700 }}>{mult}×</Box>{stepBtn(() => setMult(m => Math.min(100, m + 1)), '+')}</>)}
              <Box sx={{ flex: 1 }} />
              <Box sx={{ color: c.dim }}>Premium <b style={{ color: prem >= 0 ? c.green : c.red }}>{premLabel} {fmtMoney(Math.abs(prem), ccy)}</b></Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, mt: 0.6, color: c.dim }}>
              Margin{' '}
              {margin.loading ? <CircularProgress size={10} /> : margin.inr != null
                ? <b style={{ color: c.text }}>{ccy === 'INR' ? `₹${Math.round(margin.inr).toLocaleString('en-IN')}` : `$${(margin.inr / USD_TO_INR).toFixed(2)}`}</b>
                : <span style={{ color: c.faint }}>{margin.err || '—'}</span>}
              <Tooltip title="The app's estimate for this structure. On Delta, Portfolio margin mode usually needs less than Isolated for hedged/strangle baskets — that is an account setting on Delta itself.">
                <Box component="span" sx={{ fontSize: 10, color: c.faint, cursor: 'help' }}>ⓘ</Box>
              </Tooltip>
              <Box sx={{ flex: 1 }} />
              <span>Fees ≈ <b style={{ color: c.text }}>{fmtMoney(entryFeeUsd, ccy)}</b> <span style={{ fontSize: 10, color: c.faint }}>entry</span></span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, mt: 0.6, color: c.dim }}>
              <Tooltip title="How MKT legs are priced here. Bid/Ask: what a market order gets at the top of the book (sell at the bid, buy at the ask) — the payoff, P&L and premium then include the spread you pay. Mark: Delta's mark price (no spread). Paper orders placed here always fill by walking the live order book for the full size.">
                <span style={{ cursor: 'help' }}>Price at</span>
              </Tooltip>
              <Box sx={{ display: 'flex', bgcolor: c.hover, borderRadius: 1, p: 0.2 }}>
                {([['touch', 'Bid/Ask'], ['mark', 'Mark']] as const).map(([k, lab]) => (
                  <Box key={k} component="button" type="button" onClick={() => setPrefs({ fillModel: k })}
                    sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, px: 0.7, py: 0.1, borderRadius: 0.8, fontWeight: prefs.fillModel === k ? 700 : 500,
                      color: prefs.fillModel === k ? c.text : c.dim, bgcolor: prefs.fillModel === k ? c.panel : 'transparent' }}>{lab}</Box>
                ))}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Spread paid vs Delta's mark at the top of the book (entry only). A larger order walks the book further — the confirm dialog shows the full-size fill.">
                <span>Spread cost ≈ <b style={{ color: touchSlipUsd > 0 ? c.red : c.text }}>{fmtMoney(touchSlipUsd, ccy)}</b></span>
              </Tooltip>
            </Box>
            {heldIds.length === 1 && (
              <Box data-testid="ws-target" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.8, fontSize: 12 }}>
                <Box sx={{ color: c.dim }}>New legs go</Box>
                <Box sx={{ display: 'flex', bgcolor: c.hover, borderRadius: 1, p: 0.2 }}>
                  {([['add', `into #${heldIds[0]}`], ['new', 'a new basket']] as const).map(([k, lab]) => (
                    <Box key={k} component="button" type="button" data-target={k} onClick={() => setTarget(k)}
                      sx={{ all: 'unset', cursor: 'pointer', fontSize: 11.5, px: 0.8, py: 0.15, borderRadius: 0.8, fontWeight: target === k ? 700 : 500,
                        color: target === k ? c.text : c.dim, bgcolor: target === k ? c.panel : 'transparent' }}>{lab}</Box>
                  ))}
                </Box>
              </Box>
            )}
            {addToId == null && (
              <Box data-testid="ws-mode" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.8, fontSize: 12 }}>
                <Box sx={{ color: c.dim }}>Orders</Box>
                <Box sx={{ display: 'flex', bgcolor: c.hover, borderRadius: 1, p: 0.2 }}>
                  {(['LIVE', 'PAPER'] as const).map(k => (
                    <Tooltip key={k} disableInteractive title={k === 'LIVE' && noDeltaKey ? 'LIVE needs an active Delta API key (Settings → Brokers)' : ''}>
                      <Box component="button" type="button" data-mode={k} disabled={k === 'LIVE' && noDeltaKey} onClick={() => setOrderMode(k)}
                        sx={{ all: 'unset', cursor: k === 'LIVE' && noDeltaKey ? 'not-allowed' : 'pointer', fontSize: 11.5, px: 0.9, py: 0.15, borderRadius: 0.8, fontWeight: orderMode === k ? 800 : 500,
                          opacity: k === 'LIVE' && noDeltaKey ? 0.45 : 1,
                          color: orderMode === k ? (k === 'LIVE' ? '#fff' : c.text) : c.dim, bgcolor: orderMode === k ? (k === 'LIVE' ? c.red : c.panel) : 'transparent' }}>
                        {k === 'LIVE' ? 'LIVE — real' : 'PAPER'}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
                {orderMode === 'LIVE' && deltaAvail != null && <Box sx={{ fontSize: 11, color: c.dim }}>Delta available ₹{Math.round(deltaAvail).toLocaleString('en-IN')}</Box>}
              </Box>
            )}
            <Box component="button" type="button" data-testid="ws-place" onClick={() => setConfirm(true)}
              sx={{ all: 'unset', cursor: 'pointer', display: 'block', boxSizing: 'border-box', width: '100%', textAlign: 'center', mt: 1, py: 0.9, borderRadius: 1, fontWeight: 800, fontSize: 13.5,
                bgcolor: effMode === 'LIVE' ? c.red : c.amber, color: effMode === 'LIVE' ? '#fff' : (c.isDark ? '#111' : '#fff'), '&:hover': { filter: 'brightness(1.08)' } }}>
              {addToId != null
                ? `Add ${enabledNew.length} leg${enabledNew.length > 1 ? 's' : ''} to #${addToId} (${effMode})`
                : `Place ${effMode === 'LIVE' ? 'LIVE order' : 'PAPER order'} · ${enabledNew.length} leg${enabledNew.length > 1 ? 's' : ''}`}
            </Box>
            <Box sx={{ fontSize: 10.5, color: c.faint, textAlign: 'center', mt: 0.4 }}>
              {effMode === 'LIVE' ? 'Real market orders on Delta · protective BUY legs first' : 'Simulated fills from the live order book · no exchange order is sent'}
              {addToId == null && legs.some(l => !isNewLeg(l)) ? ' · held / roll legs excluded' : ''}
            </Box>
          </Box>
        )}

        {/* ready-made */}
        <Box sx={{ px: 1.2, pt: 1, pb: 1.2, borderTop: `1px solid ${c.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, flexWrap: 'wrap' }}>
            <Box sx={{ fontSize: 11, letterSpacing: 0.8, fontWeight: 700, color: c.dim }}>READY-MADE</Box>
            <Box sx={{ fontSize: 10.5, color: c.faint }}>{rmList.length}/{READY_MADE.length}</Box>
            <Box sx={{ flex: 1 }} />
            {(outlooks.length > 0 || costs.length > 0) && linkBtn('Clear filters', () => { setOutlooks([]); setCosts([]); }, c.dim)}
          </Box>
          <Box data-testid="rm-filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.8 }}>
            {(['Bullish', 'Bearish', 'Neutral', 'Volatile'] as Outlook[]).map(o => {
              const on = outlooks.indexOf(o) >= 0;
              const oc = o === 'Bullish' ? c.green : o === 'Bearish' ? c.red : o === 'Neutral' ? c.blue : c.amber;
              return (
                <Box key={o} component="button" type="button" data-filter={o} aria-pressed={on} onClick={() => setOutlooks(prev => toggleIn(prev, o))}
                  sx={{ all: 'unset', cursor: 'pointer', fontSize: 11.5, px: 0.9, py: 0.2, borderRadius: 5, fontWeight: on ? 700 : 500,
                    border: `1px solid ${on ? oc : c.border}`, color: on ? oc : c.dim, bgcolor: on ? `${oc}1f` : 'transparent' }}>{on ? '✓ ' : ''}{o}</Box>
              );
            })}
            <Box sx={{ width: '1px', bgcolor: c.border, mx: 0.4 }} />
            {(['Credit', 'Debit'] as Cost[]).map(k => {
              const on = costs.indexOf(k) >= 0;
              return (
                <Tooltip key={k} disableInteractive title={k === 'Credit' ? 'You receive premium when it opens (net sell). Priced on the viewed expiry now.' : 'You pay premium when it opens (net buy). Priced on the viewed expiry now.'}>
                  <Box component="button" type="button" data-filter={k} aria-pressed={on} onClick={() => setCosts(prev => toggleIn(prev, k))}
                    sx={{ all: 'unset', cursor: 'pointer', fontSize: 11.5, px: 0.9, py: 0.2, borderRadius: 5, fontWeight: on ? 700 : 500,
                      border: `1px solid ${on ? c.text : c.border}`, color: on ? c.text : c.dim, bgcolor: on ? c.hover : 'transparent' }}>{on ? '✓ ' : ''}{k}</Box>
                </Tooltip>
              );
            })}
          </Box>
          {rmList.length === 0 && <Box sx={{ fontSize: 12, color: c.dim, py: 1 }}>No ready-made matches all the selected filters.</Box>}
          <Box sx={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(auto-fill, minmax(120px, 1fr))' : 'repeat(auto-fill, minmax(190px, 1fr))', gap: 0.8 }}>
            {rmList.map(rm => {
              const oc = rm.outlook === 'Bullish' ? c.green : rm.outlook === 'Bearish' ? c.red : rm.outlook === 'Neutral' ? c.blue : c.amber;
              return (
                <Box key={rm.id} component="button" type="button" onClick={() => applyReadyMade(rm.id)}
                  sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, p: compact ? 0.6 : 0.9, borderRadius: 1, border: `1px solid ${c.border}`,
                    '&:hover': { borderColor: oc, bgcolor: c.hover } }}>
                  {!compact && (
                    <Box component="svg" viewBox="0 0 40 20" sx={{ width: 40, height: 22, flexShrink: 0, bgcolor: c.hover, borderRadius: 0.6 }}>
                      <polyline points={rm.glyph} fill="none" stroke={oc} strokeWidth="1.8" strokeLinejoin="round" />
                    </Box>
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Box sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rm.name}</Box>
                      {!compact && <Box sx={{ ml: 'auto', fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5, px: 0.5, borderRadius: 0.5, color: oc, bgcolor: `${oc}22` }}>{rm.outlook.toUpperCase()}</Box>}
                      {!compact && <Box sx={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5, px: 0.5, borderRadius: 0.5, color: c.dim, border: `1px solid ${c.border}` }}>{costOf[rm.id].toUpperCase()}</Box>}
                    </Box>
                    {!compact && <Box sx={{ fontSize: 10.5, color: c.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rm.blurb}</Box>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* confirm a new basket */}
      <Dialog open={confirm} onClose={() => !placing && setConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
          {addToId != null ? `Add ${enabledNew.length} leg${enabledNew.length > 1 ? 's' : ''} to #${addToId}` : `Place ${cls.name}`}
          <Box component="span" sx={{ fontSize: 11, fontWeight: 800, px: 0.8, borderRadius: 0.6, bgcolor: effMode === 'LIVE' ? c.red : c.amberBg, color: effMode === 'LIVE' ? '#fff' : c.amber }}>{effMode === 'LIVE' ? 'LIVE — REAL ORDERS' : 'PAPER'}</Box>
        </DialogTitle>
        <DialogContent dividers sx={{ fontSize: 13 }}>
          {effMode === 'LIVE' && (
            <Box data-testid="ws-live-note" sx={{ mb: 1.2, px: 1.2, py: 0.8, borderRadius: 1, borderLeft: `3px solid ${c.red}`, bgcolor: c.redBg, fontSize: 12.5, lineHeight: 1.5 }}>
              Places <b>real orders on Delta</b>{addToId != null ? ` into open basket #${addToId}` : ''}, in price-capped slices (below). Protective BUY legs go first; the kill switch and daily-loss cap apply.
              {deltaAvail != null && <> Delta available <b>₹{Math.round(deltaAvail).toLocaleString('en-IN')}</b>{margin.inr != null ? <> · margin est. <b>₹{Math.round(margin.inr).toLocaleString('en-IN')}</b></> : null}.</>}
            </Box>
          )}
          {enabledNew.map(l => (
            <Box key={l.id} sx={{ display: 'flex', gap: 1, py: 0.3, fontVariantNumeric: 'tabular-nums' }}>
              <b style={{ color: l.action === 'BUY' ? c.green : c.red, width: 34 }}>{l.action}</b>
              <span style={{ flex: 1 }}>{l.lots * mult} × {base} {fmtStrike(l.strike)} {l.optionType} · {expLabel(l.expiry)} <span style={{ color: c.faint }}>({+(l.lots * mult * cv).toFixed(4)} {base})</span></span>
              <Tooltip title={`Mark $${fmtPx(l.mark ?? null)}. Book fill = the size-weighted price of walking the live order book for ${l.lots * mult} lots${book.rows[l.id]?.entry?.depthShort ? ' — the visible book is thinner than this order' : ''}.`}>
                <span style={{ color: c.dim, cursor: 'help' }}>
                  {book.loading ? '…' : book.rows[l.id]?.entry ? <>book <b style={{ color: c.text }}>${fmtPx((book.rows[l.id].entry as any).price)}</b>{book.rows[l.id].entry!.depthShort ? ' ⚠' : ''}</> : <>~${fmtPx(l.price)}</>}
                  <span style={{ color: c.faint }}> · mark ${fmtPx(l.mark ?? null)}</span>
                </span>
              </Tooltip>
            </Box>
          ))}
          <Box sx={{ mt: 1.2, display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 0.4, fontSize: 12.5 }}>
            <span style={{ color: c.dim }}>Premium</span><b style={{ textAlign: 'right', color: prem >= 0 ? c.green : c.red }}>{premLabel} {fmtMoney(Math.abs(prem), ccy)}</b>
            <span style={{ color: c.dim }}>Max profit</span><b style={{ textAlign: 'right' }}>{fmtMoney(newStats.maxProfit * mult, ccy)}</b>
            <span style={{ color: c.dim }}>Max loss</span><b style={{ textAlign: 'right' }}>{fmtMoney(newStats.maxLoss * mult, ccy)}</b>
            <span style={{ color: c.dim }}>Margin (est.)</span><b style={{ textAlign: 'right' }}>{margin.inr != null ? `₹${Math.round(margin.inr).toLocaleString('en-IN')}` : '—'}</b>
            <Tooltip title={`Delta India options taker fee: ${prefs.feeRatePct}% of notional (index × size), capped at 3.5% of the premium, plus 18% GST — per side. Exit assumed at a similar premium. The basket monitor deducts the same fees from paper and live P&L, and the targets and stops are net of them.`}>
              <span style={{ color: c.dim, cursor: 'help' }}>Fees (est.) <span style={{ fontSize: 10.5 }}>entry · round trip</span></span>
            </Tooltip>
            <b style={{ textAlign: 'right' }}>{fmtMoney(entryFeeUsd, ccy)} · {fmtMoney(entryFeeUsd * 2, ccy)}</b>
            <Tooltip title="Slippage vs Delta's mark from walking the live order book for each leg's full size: entry now, and what closing the same size right now would cost. The paper engine fills at these book prices.">
              <span style={{ color: c.dim, cursor: 'help' }}>Slippage (book) <span style={{ fontSize: 10.5 }}>entry · exit</span></span>
            </Tooltip>
            <b style={{ textAlign: 'right', color: c.red }}>{bookEntrySlipUsd != null ? fmtMoney(bookEntrySlipUsd, ccy) : (book.loading ? '…' : '—')} · {exitSlipUsd != null ? fmtMoney(exitSlipUsd, ccy) : (book.loading ? '…' : '—')}</b>
            {bookNetUsd != null && <>
              <span style={{ color: c.dim }}>Premium at book fills</span>
              <b style={{ textAlign: 'right', color: bookNetUsd >= 0 ? c.green : c.red }}>{bookNetUsd >= 0 ? 'Get' : 'Pay'} {fmtMoney(Math.abs(bookNetUsd), ccy)}</b>
            </>}
          </Box>

          {addToId != null && (
            <Box sx={{ mt: 1.2, fontSize: 11.5, color: c.dim }}>
              The legs join basket #{addToId}: one position, one P&L. Its exit rules stay as they are; max profit, max loss and break-evens are re-computed with the new legs.
            </Box>
          )}
          {/* exit plan */}
          <Box sx={{ mt: 1.4, pt: 1.2, borderTop: `1px solid ${c.border}`, display: addToId != null ? 'none' : 'block' }}>
            <Box sx={{ fontSize: 11, letterSpacing: 0.6, fontWeight: 700, color: c.dim, mb: 0.6 }}>EXIT PLAN</Box>
            <Box sx={{ display: 'flex', gap: 0.8, mb: 0.8 }}>
              {(['FIXED', 'PREMIUM'] as const).map(m => (
                <Box key={m} component="button" type="button" disabled={m === 'FIXED' && creditInr <= 0} onClick={() => setExitMode(m)}
                  sx={{ all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center', py: 0.5, borderRadius: 1, fontWeight: 700, fontSize: 11.5,
                    opacity: m === 'FIXED' && creditInr <= 0 ? 0.4 : 1,
                    border: `1px solid ${(exitMode === m && (m !== 'FIXED' || creditInr > 0)) ? c.amber : c.border}`, color: exitMode === m ? c.amber : c.dim }}>
                  {m === 'FIXED' ? 'Fixed ₹ on net total P&L' : 'Premium % (app default, net)'}
                </Box>
              ))}
            </Box>
            {exitMode === 'FIXED' && creditInr > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, fontSize: 12.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  Target <Box component="input" type="number" min={10} max={100} value={prefs.tpPctOfCredit} onChange={(e: any) => setPrefs({ tpPctOfCredit: Math.max(10, Math.min(100, Number(e.target.value) || 70)) })}
                    sx={{ width: 46, p: 0.3, borderRadius: 0.6, border: `1px solid ${c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />% of credit
                  <Box sx={{ flex: 1 }} /><b style={{ color: c.green }}>+₹{tpInr.toLocaleString('en-IN')}</b>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  Stop <Box component="input" type="number" min={20} max={300} value={prefs.slPctOfCredit} onChange={(e: any) => setPrefs({ slPctOfCredit: Math.max(20, Math.min(300, Number(e.target.value) || 100)) })}
                    sx={{ width: 46, p: 0.3, borderRadius: 0.6, border: `1px solid ${c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />% of credit
                  <Box sx={{ flex: 1 }} /><b style={{ color: c.red }}>−₹{slInr.toLocaleString('en-IN')}</b>
                </Box>
                <Box data-testid="ws-net-costs" sx={{ fontSize: 12 }}>
                  Both are <b>net</b>: what you receive after costs ≈ ₹{Math.round(roundTripFeeInr + exitSlipInr).toLocaleString('en-IN')}
                  {' '}(fees ₹{Math.round(roundTripFeeInr).toLocaleString('en-IN')} round trip{exitSlipUsd != null ? ` + exit slippage ₹${Math.round(exitSlipInr).toLocaleString('en-IN')}` : book.loading ? ' + exit slippage …' : ''}). The monitor adds them.
                </Box>
                <Box sx={{ fontSize: 11, color: c.dim }}>Credit{bookNetUsd != null ? ' at the book fills' : ''} ≈ ₹{Math.round(creditInr).toLocaleString('en-IN')}. Measured on NET total P&L (adjustment bookings + open legs, valued at what closing would get, minus fees). Never re-based by a roll and never trailed automatically — drag the stop on the basket chart. Playbook: stop = full credit, target = 50–70%.</Box>
              </Box>
            ) : (
              <Box sx={{ fontSize: 11, color: c.dim }}>{creditInr <= 0 ? 'Fixed ₹ exits need a net-credit structure. ' : ''}The basket uses the app's premium-% target and stop (re-based after each adjustment).</Box>
            )}
            {newPair && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 8 }}>
                <input type="checkbox" checked={ratioAlert} onChange={e => setRatioAlert(e.target.checked)} style={{ accentColor: c.amber }} />
                Telegram alert when one short's premium reaches
                <Box component="input" type="number" step={0.5} min={1.5} max={10} value={prefs.ratioX} onChange={(e: any) => setPrefs({ ratioX: Math.max(1.5, Math.min(10, Number(e.target.value) || 3)) })}
                  sx={{ width: 42, p: 0.2, borderRadius: 0.6, border: `1px solid ${c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />× the other
              </label>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 1, fontSize: 11, color: c.faint }}>
              Fee rate
              <Box component="input" type="number" step={0.005} min={0} max={0.2} value={prefs.feeRatePct} onChange={(e: any) => setPrefs({ feeRatePct: Math.max(0, Math.min(0.2, Number(e.target.value) || 0)) })}
                sx={{ width: 54, p: 0.2, borderRadius: 0.6, border: `1px solid ${c.border}`, bgcolor: 'transparent', color: c.text, textAlign: 'right', font: 'inherit' }} />% of notional per side (check Delta's current fee page)
            </Box>
          </Box>

          {effMode === 'LIVE' ? (() => {
            // How a LIVE order goes out (2026-11-03): price-capped slices, see backend sliceExec.
            const capPct = enabledNew.map(l => book.rows[l.id]?.capPct).find(v => v != null) ?? 5;
            const short = enabledNew.filter(l => { const w = book.rows[l.id]?.withinCap; return w != null && w < l.lots * mult; });
            return (
              <Box data-testid="ws-exec-plan" sx={{ mt: 1.2, p: 0.9, borderRadius: 1, bgcolor: short.length ? c.amberBg : 'transparent', border: short.length ? 'none' : `1px solid ${c.border}`, color: c.text, fontSize: 11.5, lineHeight: 1.45 }}>
                <Box sx={{ fontSize: 11, letterSpacing: 0.6, fontWeight: 700, color: c.dim, mb: 0.4 }}>HOW IT WILL BE PLACED</Box>
                {addToId != null ? (
                  <div>The added legs go out in price-capped slices whose cap widens step by step, then at market for anything left, so the change always completes.</div>
                ) : (
                  <div>
                    In <b>price-capped slices</b>: buys first, each leg no more than <b>{capPct}%</b> of its premium beyond the best price on the book right now, with short pauses so the book can refill.
                    Within 30 s, whatever fills as whole copies of this basket is <b>kept</b> — a smaller basket that is still balanced and hedged; the rest is not placed and you are told.
                  </div>
                )}
                {short.map(l => (
                  <div key={l.id} style={{ marginTop: 3 }} data-testid="ws-exec-short">
                    {fmtStrike(l.strike)} {l.optionType}: <b>{(book.rows[l.id]!.withinCap as number).toLocaleString('en-IN')}</b> of {(l.lots * mult).toLocaleString('en-IN')} lots fit within the cap now — expect several slices{(book.rows[l.id]!.withinCap as number) === 0 ? ', and possibly no fill' : ', possibly a smaller fill'}.
                  </div>
                ))}
                {!short.length && thinBook.length > 0 && <div style={{ marginTop: 3, color: c.dim }}>Some legs are larger than the best price level; they walk a few levels, all within the cap.</div>}
                <div style={{ marginTop: 4, color: c.dim }}>Exits (target, stop, expiry square-off, Close) also go in slices, with the cap widening every few seconds, then at market for anything left — so a stop always completes.</div>
              </Box>
            );
          })() : thinBook.length > 0 && (
            <Box sx={{ mt: 1.2, p: 0.8, borderRadius: 1, bgcolor: c.amberBg, color: c.text, fontSize: 11.5 }}>
              {thinBook.map(({ l, size }) => (
                <div key={l.id}>{fmtStrike(l.strike)} {l.optionType}: {l.lots * mult} lots is more than the best {l.action === 'SELL' ? 'bid' : 'ask'} size ({size}).</div>
              ))}
              <div style={{ color: c.dim, marginTop: 4 }}>The paper order walks the book for the full size in one go (the book fill above). A LIVE order would go in price-capped slices instead.</div>
            </Box>
          )}
          <Box sx={{ mt: 1.2, fontSize: 11.5, color: c.dim }}>
            Paper fills walk each contract's live order book for the full size (the book prices above){newLegs.some(l => l.priceMode === 'LMT') ? '; your typed prices are for analysis only' : ''}. The basket then appears in Options → Baskets and the Charts payoff overlay, and the basket monitor manages it — valued at what closing would get.
            {legs.some(l => !isNewLeg(l)) ? ' Held and roll legs are not part of this order.' : ''}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={placing}>Cancel</Button>
          <Button variant="contained" color={effMode === 'LIVE' ? 'error' : 'warning'} onClick={place} disabled={placing} data-testid="ws-confirm-place">
            {placing ? <CircularProgress size={16} /> : addToId != null ? `Add to #${addToId} — ${effMode}` : effMode === 'LIVE' ? 'Place LIVE order (real)' : 'Place paper order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={liveAsk != null} onClose={() => setLiveAsk(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>LIVE {liveAsk != null && !legs.some(l => l.rollBasketId === liveAsk.id && l.enabled) ? 'leg close' : 'adjustment'} on #{liveAsk?.id}?</DialogTitle>
        <DialogContent dividers sx={{ fontSize: 13 }}>
          This sends <b>real market orders on Delta</b>: the legs marked CLOSE are closed first (shorts first), then the new legs open (BUY legs first). The booked P&L is the actual fills.
          {liveAsk != null && nakedAfter(liveAsk.id).length > 0 && (
            <Box sx={{ mt: 1, color: c.red, fontWeight: 600 }}>
              Afterwards the short {nakedAfter(liveAsk.id).join(' and ')} has no protective long: the loss is uncapped and the margin rises.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiveAsk(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { const a = liveAsk; setLiveAsk(null); if (a) executeAdj(a.id, true); }}>{liveAsk != null && !legs.some(l => l.rollBasketId === liveAsk.id && l.enabled) ? 'Close LIVE' : 'Execute LIVE adjustment'}</Button>
        </DialogActions>
      </Dialog>

      {exitDlg != null && <ExitRulesDialog id={exitDlg} b={held[exitDlg]} cv={cv} prefs={prefs} c={c} onClose={() => setExitDlg(null)} onSaved={() => { setExitDlg(null); refreshHeld(); }} />}

      <Dialog open={closeDlg != null} onClose={() => setCloseDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>Close {closeDlg != null && isLiveBasket(closeDlg) ? 'LIVE' : 'paper'} basket #{closeDlg}?</DialogTitle>
        <DialogContent dividers sx={{ fontSize: 13 }}>
          {closeDlg != null && isLiveBasket(closeDlg)
            ? <>Every leg is closed with a <b>real market order on Delta</b> (shorts first) and the P&L is booked. This can't be undone.</>
            : <>Every leg is squared off at its live mark and the P&L is booked. This can't be undone.</>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => closeBasketNow(closeDlg as number)}>Close basket</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegsPanel;
